import { useState, useEffect } from 'react';
import {
    Box,
    Text,
    Flex,
    Grid,
    VStack,
    HStack,
    Icon,
    Spinner,
    useToast,
    Tag,
    FormControl,
    Center
} from '@chakra-ui/react';
import {
    FiTrendingUp,
    FiActivity,
    FiClock,
    FiMessageCircle,
    FiBarChart2
} from 'react-icons/fi';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders, getUserTimeZone } from '../../../utils/DefaultFunction';
import { CustomToggleSwitch } from './ToggleSwitch';

import { PRIMARY_BG } from '../../../theme/brand';
export function TimelineTab({ contactId }) {
    const [engagementData, setEngagementData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isAllTime, setIsAllTime] = useState(false);
    const [bestTimeLoading, setBestTimeLoading] = useState(false);
    const [isChannelAllTime, setIsChannelAllTime] = useState(false);
    const [bestChannelLoading, setBestChannelLoading] = useState(false);
    const toast = useToast();

    useEffect(() => {
        fetchEngagement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactId]);

    useEffect(() => {
        fetchBestTimeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactId, isAllTime]);

    useEffect(() => {
        fetchBestChannelData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactId, isChannelAllTime]);

    const fetchEngagement = async () => {
        setLoading(true);
        try {
            const to = new Date().toISOString().split('T')[0];
            const from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            const url = `${process.env.REACT_APP_API_URL}/contacts/${contactId}/engagement?tz=${getUserTimeZone()}`;
            const response = await API.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                setEngagementData(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching engagement:", error);
            toast({
                title: "Error",
                description: "Failed to fetch engagement data",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchBestTimeData = async () => {
        setBestTimeLoading(true);
        try {
            const scopeParam = isAllTime ? `?scope=all_time&tz=${getUserTimeZone()}` : `?tz=${getUserTimeZone()}`;
            const url = `${process.env.REACT_APP_API_URL}/contacts/${contactId}/best-time-to-send${scopeParam}`;
            const response = await API.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                // Update the hourly breakdown and stats with new best_time_to_send
                setEngagementData(prev => ({
                    ...prev,
                    hourly_breakdown: response?.data?.data?.hourly_breakdown || prev?.hourly_breakdown || [],
                    best_time_to_send: response?.data?.data?.best_time_to_send,
                    range: response.data.data.range,
                    stats: {
                        ...prev?.stats,
                        best_time_to_send: response?.data?.data?.best_time_to_send
                    }
                }));
            }
        } catch (error) {
            console.error("Error fetching best time data:", error);
            toast({
                title: "Error",
                description: "Failed to fetch best time data",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setBestTimeLoading(false);
        }
    };

    const fetchBestChannelData = async () => {
        setBestChannelLoading(true);
        try {
            const scopeParam = isChannelAllTime ? `?scope=all_time&tz=${getUserTimeZone()}` : `?tz=${getUserTimeZone()}`;
            const url = `${process.env.REACT_APP_API_URL}/contacts/${contactId}/best-channel-by-time-of-day${scopeParam}`;
            const response = await API.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                // Update only the best channel by time data
                 setEngagementData(prev => ({
                    ...prev,
                    best_channel_by_time_of_day: response?.data?.data?.best_channel_by_time_of_day || prev?.best_channel_by_time_of_day || [],
                    recommended_channels_by_time_period: response?.data?.data?.recommended_channels_by_time_period || prev?.recommended_channels_by_time_period || {},
                    overall_best_channel: response?.data?.data?.overall_best_channel || prev?.overall_best_channel || {},
                    range: response?.data?.data?.range
                }));
            }
        } catch (error) {
            console.error("Error fetching best channel data:", error);
            toast({
                title: "Error",
                description: "Failed to fetch best channel data",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setBestChannelLoading(false);
        }
    };

    const handleScopeToggle = () => {
        setIsAllTime(!isAllTime);
    };

    const handleChannelScopeToggle = () => {
        setIsChannelAllTime(!isChannelAllTime);
    };

    if (loading || !engagementData) {
        return (
            <Box textAlign="center" py={10}>
                <Spinner size="xl" color="blue.500" />
            </Box>
        );
    }

    const formattedTimeline = (engagementData.series || []).map(item => ({
        date: new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        messages: item.messages
    }));

    const hourlyBreakdown = engagementData.hourly_breakdown || [];
    const bestChannelByTime = engagementData.best_channel_by_time_of_day || [];
    const recommendedChannels = engagementData.recommended_channels_by_time_period || {};
    const overallBestChannel = engagementData.overall_best_channel || {};
    const stats = engagementData.stats || {};
    const bestTime = stats.best_time_to_send || {};
    const bestChannelTime = stats.best_channel_and_time || {};

    const calculateMorningAvg = () => {
        const morningHours = hourlyBreakdown.filter(h => h.hour >= 8 && h.hour <= 11);
        const totalReads = morningHours.reduce((sum, h) => sum + (h.read || 0), 0);
        return morningHours.length > 0 ? Math.round(totalReads / morningHours.length) : 0;
    };

    const calculateEveningAvg = () => {
        const eveningHours = hourlyBreakdown.filter(h => h.hour >= 18 && h.hour <= 21);
        const totalReads = eveningHours.reduce((sum, h) => sum + (h.read || 0), 0);
        return eveningHours.length > 0 ? Math.round(totalReads / eveningHours.length) : 0;
    };

    const morningAvg = calculateMorningAvg();
    const eveningAvg = calculateEveningAvg();

    const areaChartOptions = {
        chart: { type: 'area', height: 400 },
        title: { text: null },
        xAxis: {
            categories: formattedTimeline.map(item => item.date),
            title: { text: 'Date' },
            lineWidth: 0
        },
        yAxis: {
            title: { text: 'Messages' },
            lineWidth: 0
        },
        tooltip: {
            shared: true,
            formatter: function () {
                return '<b>' + this.x + '</b><br/>Messages: <b>' + this.y.toLocaleString() + '</b>';
            }
        },
        plotOptions: { area: { fillOpacity: 0.3 } },
        series: [{
            name: 'Messages',
            data: formattedTimeline.map(item => item.messages),
            color: '#3b82f6'
        }],
        credits: { enabled: false }
    };

    const columnChartOptions = {
        chart: { type: 'column', height: 400 },
        title: { text: null },
        xAxis: {
            categories: hourlyBreakdown.map(item => item.label),
            title: { text: 'Hour' },
            lineWidth: 0
        },
        yAxis: {
            title: { text: 'Count' },
            lineWidth: 0
        },
        tooltip: {
            shared: true,
            formatter: function () {
                let tooltip = '<b>' + this.x + '</b><br/>';
                this.points.forEach(point => {
                    tooltip += point.series.name + ': <b>' + point.y.toLocaleString() + '</b><br/>';
                });
                return tooltip;
            }
        },
        plotOptions: { column: { borderRadius: 3 } },
        series: [
            { name: 'Messages Sent', data: hourlyBreakdown.map(item => item.sent || 0), color: '#f59e0b' },
            { name: 'Messages Delivered', data: hourlyBreakdown.map(item => item.delivered || 0), color: '#3b82f6' },
            { name: 'Messages Read', data: hourlyBreakdown.map(item => item.read || 0), color: '#10b981' },
        ],
        credits: { enabled: false }
    };



    const lineChartOptions = {
        chart: { type: 'line', height: 400 },
        title: { text: null },
        xAxis: {
            categories: bestChannelByTime.length > 0 ? bestChannelByTime[0].points.map(p => p.label) : [],
            title: { text: 'Hour' },
            lineWidth: 0
        },
        yAxis: {
            title: { text: 'Read Receipts' },
            lineWidth: 0
        },
        tooltip: { shared: true },
        plotOptions: { line: { marker: { enabled: false } } },
        series: bestChannelByTime.map(channelData => ({
            name: channelData.channel,
            data: channelData.points.map(p => p.reads || 0),
            color: getChannelColor(channelData.channel),
            lineWidth: 2
        })),
        credits: { enabled: false }
    };

    const formatPeakDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <VStack spacing={6} align="stretch">
            {/* Engagement Statistics */}
            <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
                <Box className='box-style-card'>
                    <Box pb={2}>
                        <Flex justify="space-between" align="center">
                            <Text fontSize={"sm"} fontWeight={"500"}>Avg Daily Messages</Text>
                            <Icon as={FiActivity} color="gray.500" />
                        </Flex>
                    </Box>
                    <Box>
                        <Text fontSize="2xl" fontWeight="bold">{stats.avg_daily || 0}</Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>Last 30 days</Text>
                    </Box>
                </Box>

                <Box className='box-style-card'>
                    <Box pb={2}>
                        <Flex justify="space-between" align="center">
                            <Text fontSize={"sm"} fontWeight={"500"}>Peak Day</Text>
                            <Icon as={FiTrendingUp} color="gray.500" />
                        </Flex>
                    </Box>
                    <Box>
                        <Text fontSize="2xl" fontWeight="bold">{stats.peak_day?.messages || 0}</Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                            {stats.peak_day?.date ? formatPeakDate(stats.peak_day.date) : 'N/A'}
                        </Text>
                    </Box>
                </Box>

                <Box className='box-style-card'>
                    <Box pb={2}>
                        <Flex justify="space-between" align="center">
                            <Text fontSize={"sm"} fontWeight={"500"}>Best Time to Send</Text>
                            <Icon as={FiClock} color="gray.500" />
                        </Flex>
                    </Box>
                    <Box>
                        <Text fontSize="2xl" fontWeight="bold">{bestTime.label || 'N/A'}</Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                            {bestTime.reads_per_hour || 0} reads/hour (All Channel)
                        </Text>
                    </Box>
                </Box>

                <Box className='box-style-card'>
                    <Box pb={2}>
                        <Flex justify="space-between" align="center">
                            <Text fontSize={"sm"} fontWeight={"500"}>Best Channel & Time</Text>
                            <Icon as={FiMessageCircle} color="gray.500" />
                        </Flex>
                    </Box>
                    <Box>
                        <Text fontSize="2xl" fontWeight="bold">{bestChannelTime.channel || 'N/A'}</Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                            {bestChannelTime.label || 'N/A'} - {bestChannelTime.reads_per_hour || 0} reads/hour
                        </Text>
                    </Box>
                </Box>
            </Grid>

            {/* Message Activity Timeline */}
            <Box className='box-style-card' p={4}>
                <VStack align="stretch" spacing={4}>
                    <Box>
                        <Text fontSize="md" fontWeight="600">Message Activity Timeline</Text>
                        <Text fontSize="sm" color="gray.600">Daily message volume over the last 30 days</Text>
                    </Box>
                    {formattedTimeline.length > 0 ? (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={areaChartOptions}
                        />
                    ) : (
                        <Center minH="400px">
                            <VStack spacing={3}>
                                <Icon as={FiActivity} boxSize={12} color="gray.300" />
                                <Text fontSize="md" fontWeight="medium" color="gray.500">
                                    No Activity Data
                                </Text>
                                <Text fontSize="sm" color="gray.400" textAlign="center">
                                    No message activity recorded in the last 30 days
                                </Text>
                            </VStack>
                        </Center>
                    )}
                </VStack>
            </Box>

            {/* Best Time to Send Messages with Toggle */}
            <Box className='box-style-card' p={4} position="relative">
                {bestTimeLoading && (
                    <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="whiteAlpha.800" zIndex={1} display="flex" alignItems="center" justifyContent="center" borderRadius="md">
                        <Spinner size="lg" color="blue.500" />
                    </Box>
                )}
                <VStack align="stretch" spacing={4}>
                    <Flex justify="space-between" align="center">
                        <Box>
                            <Text fontSize="md" fontWeight="600">Best Time to Send Messages</Text>
                            <Text fontSize="sm" color="gray.600">Hourly read and delivery receipt patterns</Text>
                        </Box>
                        <FormControl display="flex" alignItems="center" w="auto">

                            <CustomToggleSwitch
                                leftLabel="Last 30 Days"
                                rightLabel="All Time"
                                isChecked={isAllTime}
                                onChange={setIsAllTime}
                                size="sm"
                            />

                        </FormControl>
                    </Flex>
                    {hourlyBreakdown.length > 0 ? (
                        <>
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={columnChartOptions}
                            />

                            {/* Best Time Highlight */}
                            <Box pt={4} borderTopWidth="1px">
                                <HStack
                                    justify="space-between"
                                    bgGradient="linear(to-r, green.50, emerald.50)"
                                    borderRadius="lg"
                                    p={3}
                                    borderWidth="1px"
                                    borderColor="green.100"
                                >
                                    <HStack>
                                        <Icon as={FiClock} color="green.600" />
                                        <Text fontSize="sm" fontWeight="medium" color="gray.700">Peak Engagement Time</Text>
                                    </HStack>
                                    <HStack>
                                        <Text fontWeight="semibold">{bestTime.label || 'N/A'}</Text>
                                        <Text fontSize="sm" fontWeight="bold" color="green.600">
                                            {bestTime.reads_per_hour || 0} reads
                                        </Text>
                                    </HStack>
                                </HStack>

                                {/* Time Period Summary */}
                                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3} mt={3}>
                                    <Box bg="blue.50" borderRadius="lg" p={3} borderWidth="1px" borderColor="blue.100">
                                        <Text fontSize="xs" color="gray.600" mb={1}>Morning Peak (8-11 AM)</Text>
                                        <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                                            Avg {morningAvg} reads/hour
                                        </Text>
                                    </Box>
                                    <Box bg="purple.50" borderRadius="lg" p={3} borderWidth="1px" borderColor="purple.100">
                                        <Text fontSize="xs" color="gray.600" mb={1}>Evening Peak (6-9 PM)</Text>
                                        <Text fontSize="sm" fontWeight="semibold" color="purple.700">
                                            Avg {eveningAvg} reads/hour
                                        </Text>
                                    </Box>
                                </Grid>
                            </Box>
                        </>
                    ) : (
                        <Center minH="400px">
                            <VStack spacing={3}>
                                <Icon as={FiClock} boxSize={12} color="gray.300" />
                                <Text fontSize="md" fontWeight="medium" color="gray.500">
                                    No Hourly Data Available
                                </Text>
                                <Text fontSize="sm" color="gray.400" textAlign="center">
                                    Not enough data to determine best time to send messages
                                </Text>
                            </VStack>
                        </Center>
                    )}
                </VStack>
            </Box>

            {/* Best Channel by Time of Day */}
            {bestChannelByTime.length > 0 ? (
                <Box className='box-style-card' p={4} position="relative">
                    {bestChannelLoading && (
                        <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="whiteAlpha.800" zIndex={1} display="flex" alignItems="center" justifyContent="center" borderRadius="md">
                            <Spinner size="lg" color="blue.500" />
                        </Box>
                    )}
                    <VStack align="stretch" spacing={4}>
                        <Flex justify="space-between" align="center">
                            <Box>
                                <Text fontSize="md" fontWeight="600">Best Channel by Time of Day</Text>
                                <Text fontSize="sm" color="gray.600">Channel engagement patterns throughout the day</Text>
                            </Box>
                            <FormControl display="flex" alignItems="center" w="auto">
                                <CustomToggleSwitch
                                    leftLabel="Last 30 Days"
                                    rightLabel="All Time"
                                    isChecked={isChannelAllTime}
                                    onChange={setIsChannelAllTime}
                                    size="sm"
                                />
                            </FormControl>
                        </Flex>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={lineChartOptions}
                        />

                        {/* Channel Recommendations */}
                        <Box pt={4} borderTopWidth="1px">
                            <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={3}>
                                Recommended Channels by Time Period
                            </Text>
                            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={3}>
                                {/* Early Morning */}
                                <Box bg="amber.50" borderRadius="lg" p={3} borderWidth="1px" borderColor="amber.100">
                                    <Text fontSize="xs" color="gray.600" mb={2}>
                                        {recommendedChannels.early_morning?.label || 'Early Morning (12-6 AM)'}
                                    </Text>
                                    {recommendedChannels.early_morning?.primary ? (
                                        <HStack>
                                            <Box w="8px" h="8px" borderRadius="full" bg={getChannelColor(recommendedChannels.early_morning.primary.channel)} />
                                            <Text fontSize="sm" fontWeight="semibold">{recommendedChannels.early_morning.primary.channel}</Text>
                                            <Text fontSize="xs" color="gray.600" ml="auto">Best engagement</Text>
                                        </HStack>
                                    ) : (
                                        <Text fontSize="sm" color="gray.500">No data available</Text>
                                    )}
                                </Box>

                                {/* Business Hours */}
                                <Box bg="blue.50" borderRadius="lg" p={3} borderWidth="1px" borderColor="blue.100">
                                    <Text fontSize="xs" color="gray.600" mb={2}>
                                        {recommendedChannels.business_hours?.label || 'Business Hours (8 AM-5 PM)'}
                                    </Text>
                                    <VStack align="stretch" spacing={1}>
                                        {recommendedChannels.business_hours?.primary ? (
                                            <HStack>
                                                <Box w="8px" h="8px" borderRadius="full" bg={getChannelColor(recommendedChannels.business_hours.primary.channel)} />
                                                <Text fontSize="sm" fontWeight="semibold">{recommendedChannels.business_hours.primary.channel}</Text>
                                                <Tag size="sm" colorScheme="green" ml="auto">Primary</Tag>
                                            </HStack>
                                        ) : null}
                                        {recommendedChannels.business_hours?.secondary ? (
                                            <HStack>
                                                <Box w="8px" h="8px" borderRadius="full" bg={getChannelColor(recommendedChannels.business_hours.secondary.channel)} />
                                                <Text fontSize="sm" fontWeight="medium">{recommendedChannels.business_hours.secondary.channel}</Text>
                                                <Text fontSize="xs" color="gray.600" ml="auto">Secondary</Text>
                                            </HStack>
                                        ) : null}
                                        {!recommendedChannels.business_hours?.primary && (
                                            <Text fontSize="sm" color="gray.500">No data available</Text>
                                        )}
                                    </VStack>
                                </Box>

                                {/* Evening */}
                                <Box bg="purple.50" borderRadius="lg" p={3} borderWidth="1px" borderColor="purple.100">
                                    <Text fontSize="xs" color="gray.600" mb={2}>
                                        {recommendedChannels.evening?.label || 'Evening (6-11 PM)'}
                                    </Text>
                                    <VStack align="stretch" spacing={1}>
                                        {recommendedChannels.evening?.primary ? (
                                            <HStack>
                                                <Box w="8px" h="8px" borderRadius="full" bg={getChannelColor(recommendedChannels.evening.primary.channel)} />
                                                <Text fontSize="sm" fontWeight="semibold">{recommendedChannels.evening.primary.channel}</Text>
                                                <Tag size="sm" colorScheme="green" ml="auto">Peak</Tag>
                                            </HStack>
                                        ) : null}
                                        {recommendedChannels.evening?.secondary ? (
                                            <HStack>
                                                <Box w="8px" h="8px" borderRadius="full" bg={getChannelColor(recommendedChannels.evening.secondary.channel)} />
                                                <Text fontSize="sm" fontWeight="medium">{recommendedChannels.evening.secondary.channel}</Text>
                                                <Tag size="sm" colorScheme="blue" ml="auto">Good</Tag>
                                            </HStack>
                                        ) : null}
                                        {!recommendedChannels.evening?.primary && (
                                            <Text fontSize="sm" color="gray.500">No data available</Text>
                                        )}
                                    </VStack>
                                </Box>
                            </Grid>

                            {/* Top Channel Summary */}
                            {overallBestChannel.channel && (
                                <Box mt={4} bgGradient="linear(to-r, green.50, emerald.50)" borderRadius="lg" p={4} borderWidth="1px" borderColor="green.100">
                                    <Flex align="center" justify="space-between">
                                        <HStack>
                                            <Icon as={FiMessageCircle} color="green.600" />
                                            <Text fontSize="sm" fontWeight="medium" color="gray.700">Overall Best Channel</Text>
                                        </HStack>
                                        <HStack spacing={3}>
                                            <HStack>
                                                <Box w="12px" h="12px" borderRadius="full" bg={getChannelColor(overallBestChannel.channel)} />
                                                <Text fontWeight="semibold">{overallBestChannel.channel}</Text>
                                            </HStack>
                                            <Box textAlign="right">
                                                <Text fontSize="sm" fontWeight="bold" color="green.600">
                                                    {overallBestChannel.reads_per_hour_peak || 0} reads/hour peak
                                                </Text>
                                                <Text fontSize="xs" color="gray.600">at {overallBestChannel.best_hour_label || 'N/A'}</Text>
                                            </Box>
                                        </HStack>
                                    </Flex>
                                </Box>
                            )}
                        </Box>
                    </VStack>
                </Box>
            ) : (
                <Box className='box-style-card' p={4}>
                    <VStack align="stretch" spacing={4}>
                        <Box>
                            <Text fontSize="md" fontWeight="600">Best Channel by Time of Day</Text>
                            <Text fontSize="sm" color="gray.600">Channel engagement patterns throughout the day</Text>
                        </Box>
                        <Center minH="400px">
                            <VStack spacing={3}>
                                <Icon as={FiBarChart2} boxSize={12} color="gray.300" />
                                <Text fontSize="md" fontWeight="medium" color="gray.500">
                                    No Channel Data Available
                                </Text>
                                <Text fontSize="sm" color="gray.400" textAlign="center">
                                    Not enough data to analyze channel performance by time
                                </Text>
                            </VStack>
                        </Center>
                    </VStack>
                </Box>
            )}
        </VStack>
    );
}

// Helper function to get channel colors
function getChannelColor(channel) {
    const colorMap = {
        'WhatsApp': '#25D366',
        'Whatsapp': '#25D366',
        'Email': '#EA4335',
        'E-Mail': '#EA4335',
        'SMS': '#0088cc',
        'Push': '#ef4444',
        'Push Notification': '#ef4444',
        'In-App': '#8b5cf6',
        'In-App Notification': '#8b5cf6',
        'Voice': PRIMARY_BG,
        'Voice (IVR)': PRIMARY_BG,
    };
    return colorMap[channel] || '#6b7280';
}